/**
 * Auth TanStack Query hooks — Layer 3 of the three-layer pattern.
 *
 * Component-consumable hooks that wrap authApi calls with mutation logic.
 * All strings go through t() — no raw literals.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { authKeys } from "@/shared/lib/queryKeys";
import { createIdempotencyKey } from "@/shared/lib/axios";
import { withBase } from "@/shared/lib/basePath";
import { ErrorCode, getErrorCode } from "@/shared/lib/errorCodes";
import { setInMemoryToken, clearInMemoryToken, decodeToken, setSessionHint, clearSessionHint } from "@/shared/lib/tokenStore";
import { login as loginApi, logout as logoutApi, register as registerApi, verifyOtp, resetPassword, verifyEmailOtp, resendEmailVerificationOtp, forgotPassword } from "../api/authApi";
import type { LoginInput } from "../types";
import type { MeResponse } from "@/shared/types/user";
import { useAuthContext } from "@/shared/hooks/useAuthContext";

/**
 * Returns the current user from AuthContext.
 * AuthProvider sets user from JWT on mount — no /me/ API call.
 * Throw if called outside AuthProvider.
 */
export function useUserFromToken(): MeResponse | undefined {
  const ctx = useAuthContext();
  return ctx.status === 'authenticated' ? ctx.user : undefined;
}

// ── Mutation: login ─────────────────────────────────────────────────────────

/**
 * Authenticates user and stores access token in the correct storage.
 *
 * Flow: POST /auth/login/ → store access token → navigate to /dashboard or /admin.
 * If account is unverified (email_not_verified), redirect to /verify-email.
 * On error throws with API detail for caller to display.
 */
export function useLogin() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return useMutation({
    mutationFn: (input: LoginInput) => {
      // One key per .mutate() call (retry=0 default). Backend replays cached
      // 200 response on accidental double-submit within 24h.
      const idempotencyKey = createIdempotencyKey();
      return loginApi(input, idempotencyKey);
    },
    onSuccess: (_data) => {
      setInMemoryToken(_data.access);
      // Mark session established so AuthProvider's boot refresh runs after the
      // full reload below (in-memory token is wiped on reload by design).
      setSessionHint();
      // Decode to route admin vs publisher before AuthProvider's /me/ round-trip.
      // window.location forces full app reinit — AuthProvider mount runs fresh
      // with token already in memory, so there is no race with GuestGuard/AuthGuard.
      const decoded = decodeToken(_data.access);
      const isAdmin = Boolean(decoded?.isStaff);
      qc.invalidateQueries({ queryKey: authKeys.all });
      // Honor ?next= for publishers (e.g. returning to /billing/success to
      // reconcile a paid checkout). Only same-origin absolute paths — never a
      // protocol-relative "//host" — are allowed, to block open redirects.
      const next = searchParams.get("next");
      const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      const dest = isAdmin ? "/admin" : (safeNext ?? "/dashboard");
      window.location.replace(withBase(dest));
    },
    onError: (error: unknown, variables: LoginInput) => {
      const code = getErrorCode(error);
      if (code === ErrorCode.EMAIL_NOT_VERIFIED) {
        sessionStorage.setItem("verify_email", variables.email);
        sessionStorage.setItem("verify_email_back", "/login");
        navigate("/verify-email", { replace: true });
        return;
      }
      // ACCOUNT_PENDING and other errors propagate via mutation.error;
      // callers read `mutation.error` (an ApiError) for the code/detail.
      // No throw needed inside onError — TanStack swallows it anyway.
    },
  });
}

// ── Mutation: register ─────────────────────────────────────────────────────

/**
 * Registers a new publisher account and navigates to /verify-email on success.
 *
 * Flow: POST /auth/register/ → navigate to email verification page.
 * On error surfaces the API error message inline.
 */
export function useRegister() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: Parameters<typeof registerApi>[0]) => {
      const idempotencyKey = createIdempotencyKey();
      return registerApi(input, idempotencyKey);
    },
    onSuccess: (_data, variables) => {
      sessionStorage.setItem("verify_email", variables.email);
      sessionStorage.setItem("verify_email_back", "/register");
      navigate("/verify-email");
    },
    onError: (error: { response?: { data?: { detail?: string } } }) => {
      const msg = error.response?.data?.detail ?? "Registration failed";
      throw new Error(msg);
    },
  });
}

// ── Mutation: verify email OTP ────────────────────────────────────────────────

/**
 * Validates the 6-digit OTP sent to the user's email during registration.
 *
 * Flow: POST /auth/verify-email-otp/ → success returns void (component navigates).
 */
export function useVerifyEmailOtp() {
  return useMutation({
    mutationFn: ({ email, otp }: { email: string; otp: string }) =>
      verifyEmailOtp(email, otp),
    onError: (error: { response?: { data?: { detail?: string } } }) => {
      const msg = error.response?.data?.detail ?? "Verification failed";
      throw new Error(msg);
    },
  });
}

// ── Mutation: resend email verification OTP ──────────────────────────────────

/**
 * Requests a new OTP be sent to the user's email.
 * Always succeeds (no enumeration).
 */
export function useResendEmailVerificationOtp() {
  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      resendEmailVerificationOtp(email),
  });
}

/**
 * Requests a new password-reset OTP be sent to the user's email.
 * Always succeeds (no enumeration).
 */
export function useResendPasswordOtp() {
  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      forgotPassword(email),
  });
}

// ── Mutation: logout ───────────────────────────────────────────────────────

/**
 * Logs out the current user — clears localStorage token and backend session.
 *
 * Flow:
 * 1. Best-effort FCM unsubscribe for this browser (so backend stops sending pushes here).
 * 2. POST /auth/logout/ → backend blacklists refresh token + deactivates this session's device.
 * 3. Clear access token + navigate to /login.
 *
 * The FCM unsubscribe in step 1 is fire-and-forget. Failures don't block logout;
 * the backend's session-linked deactivation in step 2 acts as the belt to the
 * frontend's suspenders.
 */
export function useLogout() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Import lazily to avoid circular dependency — auth ↔ notifications.
      const { unsubscribeFromFcm } = await import("@/features/notifications/api/fcmApi");
      const { notificationKeys } = await import("@/shared/lib/queryKeys");

      // Read the active registrationId from query cache (no extra network call).
      const devices = qc.getQueryData<Array<{ registrationId: string; active: boolean }>>(
        notificationKeys.devices()
      );
      const activeToken = devices?.find((d) => d.active)?.registrationId;
      if (activeToken) {
        try {
          await unsubscribeFromFcm(activeToken);
        } catch {
          // Non-blocking — backend deactivate_by_jti is the reliable path.
        }
      }

      return logoutApi();
    },
    onSettled: () => {
      clearInMemoryToken();
      clearSessionHint();
      localStorage.setItem("auth:logout", Date.now().toString());
      qc.clear();
      window.location.href = withBase("/login");
    },
  });
}

// ── Mutation: verify OTP ───────────────────────────────────────────────────

/**
 * Checks if a 6-digit OTP is valid for a given email.
 * Does NOT consume the OTP — use useResetPassword for the actual reset.
 */
export function useVerifyOtp() {
  return useMutation({
    mutationFn: ({ email, otp }: { email: string; otp: string }) =>
      verifyOtp(email, otp),
  });
}

// ── Mutation: reset password ────────────────────────────────────────────────

/**
 * Validates OTP and sets new password.
 *
 * Flow: POST /auth/reset-password/ → clear sessionStorage → navigate to /login.
 */
export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ email, otp, newPassword }: { email: string; otp: string; newPassword: string }) =>
      resetPassword(email, otp, newPassword),
    onSuccess: () => {
      sessionStorage.removeItem("reset_email");
      navigate("/login");
    },
    onError: (error: { response?: { data?: { detail?: string } } }) => {
      const msg = error.response?.data?.detail ?? "Reset failed";
      throw new Error(msg);
    },
  });
}
